import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { buildApiUrl } from '../utils/api';

// ACCIONES
export const setDataForProcedure = createAction('columns/setDataForProcedure');
// Se eliminó la declaración redundante de setTotalRecords mediante createAction

// THUNKS
export const fetchUnifiedGroupData = createAsyncThunk(
  'columns/fetchUnifiedGroupData',
  async (
    {
      unique_key,
      fechaInicio = '2024-04-01',
      fechaFin = '2100-12-31',
      perPage = 10,
      p_page = 1,
      p_field_filter = "",
      p_column_filter = ""
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await axios.get(buildApiUrl('/get_unified_data'), {
        params: {
          unique_key,
          fechaInicio,
          fechaFin,
          paginacion: perPage,
          p_page,
          p_field_filter,
          p_column_filter,
        },
        headers: { 'X-WP-Nonce': autocrm_globals.nonce },
      });

      const { columns, rows } = response.data;
      const totalRecords =
        response.data.totalRecords ||
        (rows.length > 0 && rows[0].totalRecords ? rows[0].totalRecords : 0);

      if (!Array.isArray(columns) || !Array.isArray(rows)) {
        throw new Error('La respuesta no contiene la estructura completa (columns y rows)');
      }
      console.log("🛠️ [Redux] Datos recibidos de API en fetchUnifiedGroupData:", response.data.columns);

      dispatch(setTotalRecords({ unique_key, totalRecords }));
      return { unique_key, columns, rows, totalRecords };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchGroupData = createAsyncThunk(
  'columns/fetchGroupData',
  async (
    {
      unique_key,
      order_id = null,
      perPage = 10000,
      p_page = 1,
      p_field_filter = "",
      p_column_filter = ""
    },
    { rejectWithValue, dispatch }
  ) => {
    dispatch(setLoadingMode('group'));
    try {
      let params = new URLSearchParams({
        unique_key,
        'parameters[fechaInicio]': '2025-04-01',
        'parameters[fechaFin]': '2100-12-31',
        'parameters[perPage]': perPage.toString(),
        'parameters[p_page]': p_page.toString(),
      });
      if (order_id) {
        params.append('parameters[order_id]', order_id);
      }
      if (p_field_filter) {
        params.append('parameters[p_field_filter]', p_field_filter);
      }
      if (p_column_filter) {
        params.append('parameters[p_column_filter]', p_column_filter);
      }
      const response = await axios.get(buildApiUrl('/get_unified_data') + '?' + params.toString(), {
        headers: {
          'X-WP-Nonce': autocrm_globals.nonce,
          'Content-Type': 'application/json',
        }
      });
      const { columns, rows, group_name } = response.data;
      if (!columns || !rows) {
        throw new Error('La respuesta no contiene la estructura esperada (columns y rows)');
      }
      return { unique_key, columns, rows, group_name };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateSingleRow = createAsyncThunk(
  'columns/updateSingleRow',
  async ({ unique_key, order_id, updatedRow }, { getState }) => {
    return { unique_key, order_id, updatedRow };
  }
);

export const updateOrderStatus = createAsyncThunk(
  'columns/updateOrderStatus',
  async ({ unique_key, order_id, new_status }, { rejectWithValue, dispatch }) => {
    dispatch(setLoadingMode('updateOrderStatus'));
    try {
      const response = await axios.post(
        buildApiUrl('/update_order_status'),
        { unique_key, order_id, new_status },
        {
          headers: {
            'X-WP-Nonce': autocrm_globals.nonce,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al actualizar el estado.');
      }
      return { unique_key, order_id, updatedRow: response.data.row_data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchColumnConfigurations = createAsyncThunk(
  'columns/fetchColumnConfigurations',
  async (unique_key, { rejectWithValue, dispatch }) => {
    dispatch(setLoadingMode('columnConfigurations'));
    try {
      const response = await axios.get(buildApiUrl('/get_column_configurations'), {
        params: { unique_key },
        headers: { 'X-WP-Nonce': autocrm_globals.nonce },
      });
      return { unique_key, settings: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchColumnAliases = createAsyncThunk(
  'columns/fetchColumnAliases',
  async ({ unique_key }, { rejectWithValue, dispatch }) => {
    dispatch(setLoadingMode('columnAliases'));
    try {
      const response = await axios.get(buildApiUrl('/get_column_aliases'), {
        params: { unique_key },
        headers: { 'X-WP-Nonce': autocrm_globals.nonce },
      });
      return { unique_key, aliases: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchDashboardGroups = createAsyncThunk(
  'columns/fetchDashboardGroups',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(setLoadingMode('dashboardGroups'));
    try {
      const response = await axios.get(buildApiUrl('/dashboard_groups'), {
        headers: { 'X-WP-Nonce': autocrm_globals.nonce },
      });
      const rawGroups = response.data;
      const groups = rawGroups.map(g => ({
        ...g,
        procedures: g.procedures ? JSON.parse(g.procedures) : [],
        columns: g.columns ? JSON.parse(g.columns) : [],
        parameters: g.parameters ? JSON.parse(g.parameters) : {},
        data: g.data ? JSON.parse(g.data) : []
      }));
      for (const group of groups) {
        if (group.unique_key) {
          try {
            const columnSettingsResponse = await dispatch(fetchColumnConfigurations(group.unique_key)).unwrap();
            if (columnSettingsResponse.settings) {
              const columnSettings = columnSettingsResponse.settings;
              group.columns = group.columns.map(col => {
                const cfg = columnSettings[col.column_name] || {};
                const type = (cfg.column_type || 'Informativo').toLowerCase();
                return {
                  ...col,
                  alias: cfg.alias || col.column_name,
                  column_type: cfg.column_type || 'Informativo',
                  options: cfg.options || null,
                  default_value: cfg.default_value || '',
                  is_editable: type !== 'informativo',
                  max_length: cfg.max_length || null,
                  procedure_name: col.procedure_name || "",
                  order: cfg.order !== undefined ? cfg.order : col.order !== undefined ? col.order : 9999,
                };
              });
            }
          } catch (err) {
            // Error manejado de forma silenciosa en producción.
          }
        }
      }
      return groups;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createDashboardGroup = createAsyncThunk(
  'columns/createDashboardGroup',
  async (payload, { rejectWithValue, dispatch }) => {
    dispatch(setLoadingMode('createDashboardGroup'));
    try {
      const response = await axios.post(
        buildApiUrl('/dashboard_groups'),
        payload,
        {
          headers: {
            'X-WP-Nonce': autocrm_globals.nonce,
            'Content-Type': 'application/json',
          },
        }
      );
      if (response.data.success && response.data.id) {
        await dispatch(fetchDashboardGroups());
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateDashboardGroup = createAsyncThunk(
  'columns/updateDashboardGroup',
  async ({ id, ...payload }, { rejectWithValue, dispatch }) => {
    dispatch(setLoadingMode('updateDashboardGroup'));
    try {
      const response = await axios.put(
        buildApiUrl('/dashboard_groups'),
        { id, ...payload },
        {
          headers: {
            'X-WP-Nonce': autocrm_globals.nonce,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const saveDashboardGroup = createAsyncThunk(
  'columns/saveDashboardGroup',
  async (payload, { rejectWithValue, dispatch }) => {
    dispatch(setLoadingMode('saveDashboardGroup'));
    try {
      const { data, ...payloadWithoutData } = payload;
      const response = await axios.post(
        buildApiUrl('/guardar_dashboard_group'),
        payloadWithoutData,
        {
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': autocrm_globals.nonce,
          },
        }
      );
      await dispatch(fetchDashboardGroups());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchInitialData = createAsyncThunk(
  'columns/fetchInitialData',
  async (payload, { rejectWithValue, dispatch }) => {
    dispatch(setLoadingMode('initial'));
    try {
      const requestParams = payload.params || {
        fechaInicio: payload.fechaInicio,
        fechaFin: payload.fechaFin,
        perPage: payload.perPage,
        p_page: payload.p_page || 1,
        p_field_filter: payload.p_field_filter || "",
        p_column_filter: payload.p_column_filter || ""
      };
      const url = buildApiUrl('/execute_procedure');
      const response = await axios.post(
        url,
        {
          database: payload.database,
          procedure: payload.procedure,
          params: requestParams,
        },
        { headers: { 'X-WP-Nonce': autocrm_globals.nonce } }
      );
      if (response.data.error) {
        return rejectWithValue(response.data.message || 'Error al ejecutar el procedimiento.');
      }
      const rawData = Array.isArray(response.data.data) ? response.data.data : [];
      const totalRecords =
        response.data.totalRecords ||
        (rawData.length > 0 && rawData[0].totalRecords ? rawData[0].totalRecords : 0);
      dispatch(setTotalRecords({ unique_key: payload.unique_key, totalRecords }));
      return {
        procedure: payload.procedure,
        columns: response.data.columns,
        data: rawData,
        totalRecords,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchAllColumns = createAsyncThunk(
  'columns/fetchAllColumns',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(setLoadingMode('allColumns'));
    try {
      const response = await axios.get(buildApiUrl('/get_all_columns'), {
        headers: { 'X-WP-Nonce': autocrm_globals.nonce },
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchSavedTables = createAsyncThunk(
  'columns/fetchSavedTables',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(setLoadingMode('savedTables'));
    try {
      const response = await axios.get(buildApiUrl('/get_saved_tables'), {
        headers: { 'X-WP-Nonce': autocrm_globals.nonce },
      });
      const groupsData = Array.isArray(response.data) ? response.data : [];
      const allGroups = groupsData.map((group) => ({
        unique_key: group.unique_key,
        group_name: group.group_name || 'Sin Nombre',
        database: group.database,
        procedures: Array.isArray(group.procedures) ? group.procedures : [],
        columns: Array.isArray(group.columns)
          ? group.columns.map((col) => ({
              column_name: col.column_name,
              procedure_name: col.procedure_name,
            }))
          : [],
        parameters: group.parameters || {},
      }));
      return allGroups;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const saveColumnAlias = createAsyncThunk(
  'columns/saveColumnAlias',
  async ({ columnName, alias }, { rejectWithValue, getState, dispatch }) => {
    dispatch(setLoadingMode('saveColumnAlias'));
    try {
      const state = getState();
      const unique_key = state.columns.unique_key;
      if (!unique_key) {
        throw new Error('unique_key no está establecido en el estado.');
      }
      const currentAliases = state.columns.columnAliases?.[unique_key] || {};
      if (Object.values(currentAliases).includes(alias)) {
        return rejectWithValue(`El alias "${alias}" ya está en uso en este grupo.`);
      }
      const updatedAliases = { ...currentAliases, [columnName]: alias };
      await axios.post(
        buildApiUrl('/save_column_aliases'),
        { unique_key, aliases: updatedAliases },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': autocrm_globals.nonce,
          },
        }
      );
      return { columnName, alias, unique_key };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const saveColumnSettings = createAsyncThunk(
  'columns/saveColumnSettings',
  async (payload, { rejectWithValue, getState, dispatch }) => {
    dispatch(setLoadingMode('saveColumnSettings'));
    try {
      const unique_key = payload.unique_key;
      if (!unique_key) {
        throw new Error('unique_key no está establecido en el estado.');
      }

      console.log("🟡 [saveColumnSettings] Payload recibido:", payload);

      let normalizedSettings;
      if (payload.columnSettings) {
        normalizedSettings = payload.columnSettings;
      } else if (payload.settings) {
        normalizedSettings = Object.entries(payload.settings).reduce((acc, [columnName, config]) => {
          acc[columnName] = {
            column_name: columnName,
            column_type: config.column_type || "Informativo",
            options: Array.isArray(config.options)
              ? config.options
              : (typeof config.options === "string" ? JSON.parse(config.options) : []),
            default_value: config.default_value || "",
            max_length: config.max_length ?? 255,
            is_editable: config.is_editable ?? true,
            unique_key,
          };
          return acc;
        }, {});
      }

      console.log("🟠 [saveColumnSettings] Payload normalizado:", {
        unique_key,
        columnSettings: normalizedSettings
      });

      const response = await axios.post(
        buildApiUrl('/save_column_configurations'),
        { unique_key, columnSettings: normalizedSettings },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': autocrm_globals.nonce,
          },
        }
      );

      console.log("✅ [saveColumnSettings] Respuesta del backend:", response.data);

      return { unique_key, columnSettings: normalizedSettings };
    } catch (error) {
      console.error("❌ [saveColumnSettings] Error:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);



export const saveColumns = createAsyncThunk(
  'columns/saveColumns',
  async (payload, { rejectWithValue, dispatch }) => {
    dispatch(setLoadingMode('saveColumns'));
    try {
      const response = await axios.post(
        buildApiUrl('/save_columns'),
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': autocrm_globals.nonce,
          },
        }
      );
      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al guardar las columnas.');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteSavedTable = createAsyncThunk(
  'columns/deleteSavedTable',
  async (unique_key, { rejectWithValue, dispatch }) => {
    dispatch(setLoadingMode('deleteSavedTable'));
    try {
      const response = await axios.delete(buildApiUrl(`/delete_table/${encodeURIComponent(unique_key)}`), {
        headers: { 'X-WP-Nonce': autocrm_globals.nonce },
      });
      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al eliminar la configuración.');
      }
      return unique_key;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateDynamicColumn = createAsyncThunk(
  'columns/updateDynamicColumn',
  async ({ unique_key, order_id, column_name, new_value }, { rejectWithValue, dispatch }) => {
    dispatch(setLoadingMode('updateDynamicColumn'));
    try {
      const response = await axios.put(
        buildApiUrl('/columns'),
        { unique_key, order_id, column_name, new_value },
        { headers: { 'X-WP-Nonce': autocrm_globals.nonce } }
      );
      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al actualizar la columna dinámica.');
      }
      return { unique_key, order_id, column_name, new_value };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchOrderStatuses = createAsyncThunk(
  'columns/fetchOrderStatuses',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(setLoadingMode('orderStatuses'));
    try {
      const response = await axios.get(buildApiUrl('/get_order_statuses'), {
        headers: { 'X-WP-Nonce': autocrm_globals.nonce },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchProductList = createAsyncThunk(
  'columns/fetchProductList',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(buildApiUrl('/get_products'), {
        headers: { 'X-WP-Nonce': autocrm_globals.nonce },
      });

      console.log("🧪 Resultado de /get_products:", response.data);

      // Verifica qué propiedad contiene los productos realmente
      if (Array.isArray(response.data)) {
        return response.data;
      }

      if (Array.isArray(response.data.products)) {
        return response.data.products;
      }

      return []; // fallback
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Estado inicial
const initialState = {
  productList: [],
  loadingProducts: false,
  errorProducts: null,
  columns: [],
  dashboardColumns: {},
  selectedColumns: {},
  manuallySelectedColumns: {},
  data: {},
  dataByunique_key: {},
  totalRecordsByKey: {},
  savedTables: [],
  unique_key: '',
  parameters: { fechaInicio: '2024-04-01', fechaFin: '2100-12-31', paginacion: 10 },
  loading: false,
  loadingMode: null,
  error: null,
  savedTablesLoading: false,
  savedTablesError: null,
  saveColumnsLoading: false,
  saveColumnsError: null,
  deleteTableLoading: false,
  deleteTableError: null,
  saveColumnSettingsLoading: false,
  saveColumnSettingsError: null,
  columnSettings: {},
  columnAliases: {},
  dashboardGroups: [],
  loadingDashboardGroups: false,
  errorDashboardGroups: null,
  savingDashboardGroup: false,
  errorSavingDashboardGroup: null,
  updateRowLoading: false,
  updateRowError: null,
  orderStatuses: {},
  orderStatusesError: null,
};

const columnsSlice = createSlice({
  name: 'columns',
  initialState,
  reducers: {
    setLoadingMode: (state, action) => {
      state.loadingMode = action.payload;
    },
    setunique_key: (state, action) => {
      state.unique_key = action.payload;
      state.columnAliases = {};
      state.columnSettings = {};
      state.selectedColumns = {};
      state.manuallySelectedColumns = {};
      state.parameters = { fechaInicio: '2024-04-01', fechaFin: '2100-12-31', paginacion: 10 };
    },
    setSelectedColumns: (state, action) => {
      const { procedure, columns } = action.payload;
      if (!state.selectedColumns[procedure]) {
        state.selectedColumns[procedure] = [];
      }
      state.selectedColumns[procedure] = [...new Set(columns || [])];
    },
    addSelectedColumn: (state, action) => {
      const { procedure, column } = action.payload;
      if (!state.selectedColumns[procedure]) {
        state.selectedColumns[procedure] = [];
      }
      if (!state.selectedColumns[procedure].includes(column)) {
        state.selectedColumns[procedure].push(column);
      }
    },
    removeSelectedColumn: (state, action) => {
      const { procedure, column } = action.payload;
      if (state.selectedColumns[procedure]) {
        state.selectedColumns[procedure] = state.selectedColumns[procedure].filter(
          (col) => col !== column
        );
      }
    },
    toggleManualSelection: (state, action) => {
      const { procedure, column } = action.payload;
      if (!state.manuallySelectedColumns[procedure]) {
        state.manuallySelectedColumns[procedure] = [];
      }
      const isSelected = state.manuallySelectedColumns[procedure].includes(column);
      if (isSelected) {
        state.manuallySelectedColumns[procedure] =
          state.manuallySelectedColumns[procedure].filter((col) => col !== column);
      } else {
        state.manuallySelectedColumns[procedure].push(column);
      }
    },
    setData: (state, action) => {
      state.data = action.payload;
    },
    setColumnSettings: (state, action) => {
      const { columnName, settings } = action.payload;
      state.columnSettings[state.unique_key] = {
        ...state.columnSettings[state.unique_key],
        [columnName]: settings,
      };
    },
    setAllColumnSettings: (state, action) => {
      state.columnSettings[state.unique_key] = { ...action.payload };
    },
    setParameters: (state, action) => {
      state.parameters = { ...state.parameters, ...action.payload };
    },
    importarTablas: (state, action) => {
      state.savedTables = action.payload;
    },
    updateGroupColumns: (state, action) => {
      const { unique_key, newColumns } = action.payload;
      state.dashboardGroups = state.dashboardGroups.map((table) =>
        table.unique_key === unique_key ? { ...table, columns: newColumns } : table
      );
    },
    setDashboardColumns: (state, action) => {
      const { unique_key, columns } = action.payload;
      state.dashboardColumns[unique_key] = columns.reduce((acc, col) => {
        acc[col.column_name] = col;
        return acc;
      }, {});
    },
    updateCellValue: (state, action) => {
      const { unique_key, field, newValue } = action.payload;
      const group = state.dashboardGroups.find((g) => g.unique_key === unique_key);
      if (group && Array.isArray(group.data)) {
        group.data = group.data.map((row) => ({ ...row, [field]: newValue }));
      }
      if (state.dataByunique_key[unique_key] && Array.isArray(state.dataByunique_key[unique_key].rows)) {
        state.dataByunique_key[unique_key].rows = state.dataByunique_key[unique_key].rows.map((row) => ({
          ...row,
          [field]: newValue,
        }));
      }
    },
    updateColumnValue: (state, action) => {
      const { rowId, columnName, newValue } = action.payload;
      if (Array.isArray(state.data)) {
        const row = state.data.find((row) => row.order_id == rowId);
        if (row) {
          row[columnName] = newValue;
        }
      }
      Object.keys(state.dataByunique_key).forEach((unique_key) => {
        const group = state.dataByunique_key[unique_key];
        if (group && Array.isArray(group.rows)) {
          group.rows = group.rows.map((row) =>
            row.order_id == rowId ? { ...row, [columnName]: newValue } : row
          );
        }
      });
    },
    updateRowData: (state, action) => {
      const { unique_key, order_id, newData } = action.payload;
      if (state.dataByunique_key[unique_key] && Array.isArray(state.dataByunique_key[unique_key].rows)) {
        state.dataByunique_key[unique_key].rows = state.dataByunique_key[unique_key].rows.map(
          (row) => (row.order_id == order_id ? { ...row, ...newData } : row)
        );
      }
    },
    clearDataForKey(state, action) {
      const unique_key = action.payload;
      if (state.dataByunique_key[unique_key]) {
        state.dataByunique_key[unique_key].rows = [];
      }
    },
    updateSingleRowLocally(state, action) {
      const { unique_key, rowId, updates } = action.payload;
      const groupData = state.dataByunique_key[unique_key];
      if (!groupData || !Array.isArray(groupData.rows)) return;
      groupData.rows = groupData.rows.map((row) =>
        row.order_id === rowId ? { ...row, ...updates } : row
      );
    },
    // NUEVA VERSIÓN de addRowToTable
    addRowToTable: (state, action) => {
      const newRow = action.payload;
      const unique_key = newRow.unique_key || 'default-key';

      if (!state.dataByunique_key[unique_key]) {
        state.dataByunique_key[unique_key] = {
          rows: [],
          columns: [],
          totalRecords: 0,
        };
      }

      const rows = state.dataByunique_key[unique_key].rows;
      const existingIndex = rows.findIndex((row) => row.order_id === newRow.order_id);

      if (existingIndex !== -1) {
        rows[existingIndex] = { ...rows[existingIndex], ...newRow };
      } else {
        rows.unshift(newRow);
        state.dataByunique_key[unique_key].totalRecords += 1;
      }
    },
    setTotalRecords: (state, action) => {
      const { unique_key, totalRecords } = action.payload;
      state.totalRecordsByKey[unique_key] = totalRecords;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProductList.pending, (state) => {
        console.log("⏳ fetchProductList.pending...");
        state.loadingProducts = true;
        state.errorProducts = null;
      })
      .addCase(fetchProductList.fulfilled, (state, action) => {
        console.log("✅ fetchProductList.fulfilled:", action.payload);
        state.loadingProducts = false;
        state.productList = action.payload;
      })
      .addCase(fetchProductList.rejected, (state, action) => {
        state.loadingProducts = false;
        state.errorProducts = action.payload;
      })
      .addCase(setDataForProcedure, (state, action) => {
        const { procedure, columns, data } = action.payload;
        state.data[procedure] = { columns, data };
      })
      .addCase(fetchInitialData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInitialData.fulfilled, (state, action) => {
        const { procedure, columns, data, totalRecords } = action.payload;
        state.loading = false;
        state.data[procedure] = { columns, data };
        state.totalRecordsByKey[state.unique_key] = totalRecords;
      })
      .addCase(fetchInitialData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchGroupData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupData.fulfilled, (state, action) => {
        const { unique_key, columns, rows, group_name } = action.payload;
        state.loading = false;
        state.dataByunique_key[unique_key] = {
          columns: columns || [],
          rows: rows || [],
          groupName: group_name || ''
        };
      })
      .addCase(fetchGroupData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUnifiedGroupData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnifiedGroupData.fulfilled, (state, action) => {
        const { unique_key, columns, rows, totalRecords } = action.payload;
        state.loading = false;
        state.dataByunique_key[unique_key] = { columns, rows, totalRecords };
      })
      .addCase(fetchUnifiedGroupData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllColumns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllColumns.fulfilled, (state, action) => {
        state.loading = false;
        state.columns = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchAllColumns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchSavedTables.pending, (state) => {
        state.savedTablesLoading = true;
        state.savedTablesError = null;
      })
      .addCase(fetchSavedTables.fulfilled, (state, action) => {
        state.savedTablesLoading = false;
        state.savedTables = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchSavedTables.rejected, (state, action) => {
        state.savedTablesLoading = false;
        state.savedTablesError = action.payload;
      })
      .addCase(fetchColumnAliases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchColumnAliases.fulfilled, (state, action) => {
        const { unique_key, aliases } = action.payload;
        state.columnAliases = state.columnAliases || {};
        state.columnAliases[unique_key] = aliases;
        state.loading = false;
      })
      .addCase(fetchColumnAliases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchColumnConfigurations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchColumnConfigurations.fulfilled, (state, action) => {
        const { unique_key, settings } = action.payload;
        state.columnSettings = state.columnSettings || {};
        state.columnSettings[unique_key] = settings;
        state.loading = false;
      })
      .addCase(fetchColumnConfigurations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(saveColumnAlias.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveColumnAlias.fulfilled, (state, action) => {
        const { columnName, alias, unique_key } = action.payload;
        if (!state.columnAliases[unique_key]) {
          state.columnAliases[unique_key] = {};
        }
        state.columnAliases[unique_key][columnName] = alias;
        state.loading = false;
      })
      .addCase(saveColumnAlias.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(saveColumnSettings.pending, (state) => {
        state.saveColumnSettingsLoading = true;
        state.error = null;
      })
      .addCase(saveColumnSettings.fulfilled, (state, action) => {
        state.saveColumnSettingsLoading = false;

        console.log("🟢 [Redux fulfilled] Columna(s) guardada(s) con éxito:", {
          unique_key: state.unique_key,
          columnas: Object.keys(action.payload.columnSettings || {})
        });

        if (action.payload.columnSettings) {
          const normalizedSettings = action.payload.columnSettings;
          const targetGroupIndex = state.dashboardGroups.findIndex(
            group => group.unique_key === state.unique_key
          );
          if (targetGroupIndex !== -1) {
            state.dashboardGroups[targetGroupIndex].columns = normalizedSettings;
          }
        } else if (action.payload.settings) {
          const settingsObject = action.payload.settings;
          Object.entries(settingsObject).forEach(([colName, colConfig]) => {
            if (!state.columnSettings[state.unique_key]) {
              state.columnSettings[state.unique_key] = {};
            }
            state.columnSettings[state.unique_key][colName] = colConfig;
            const targetGroup = state.dashboardGroups.find(
              group => group.unique_key === state.unique_key
            );
            if (targetGroup) {
              const updatedColumn = {
                column_name: colName,
                ...colConfig,
                procedure_name: targetGroup.columns.find(
                  col => col.column_name === colName
                )?.procedure_name || ""
              };
              targetGroup.columns = targetGroup.columns.map(col =>
                col.column_name === colName ? updatedColumn : col
              );
            }
          });
        } else {
          const { columnName, settings } = action.payload;
          if (!state.columnSettings[state.unique_key]) {
            state.columnSettings[state.unique_key] = {};
          }
          state.columnSettings[state.unique_key][columnName] = settings;
          const targetGroup = state.dashboardGroups.find(
            group => group.unique_key === state.unique_key
          );
          if (targetGroup) {
            const updatedColumn = {
              column_name: columnName,
              ...settings,
              procedure_name: targetGroup.columns.find(
                col => col.column_name === columnName
              )?.procedure_name || ""
            };
            targetGroup.columns = targetGroup.columns.map(col =>
              col.column_name === columnName ? updatedColumn : col
            );
          }
        }
      })
      .addCase(saveColumnSettings.rejected, (state, action) => {
        state.saveColumnSettingsLoading = false;
        state.error = action.payload;
      })
      .addCase(saveColumns.pending, (state) => {
        state.saveColumnsLoading = true;
        state.saveColumnsError = null;
      })
      .addCase(saveColumns.fulfilled, (state) => {
        state.saveColumnsLoading = false;
      })
      .addCase(saveColumns.rejected, (state, action) => {
        state.saveColumnsLoading = false;
        state.saveColumnsError = action.payload;
      })
      .addCase(deleteSavedTable.pending, (state) => {
        state.deleteTableLoading = true;
        state.deleteTableError = null;
      })
      .addCase(deleteSavedTable.fulfilled, (state, action) => {
        state.deleteTableLoading = false;
        state.savedTables = state.savedTables.filter(
          table => table.unique_key !== action.payload
        );
      })
      .addCase(deleteSavedTable.rejected, (state, action) => {
        state.deleteTableLoading = false;
        state.deleteTableError = action.payload;
      })
      .addCase(updateOrderStatus.pending, (state) => {
        state.updateRowLoading = true;
        state.updateRowError = null;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        const { unique_key, order_id, updatedRow } = action.payload;
        if (!unique_key || !order_id || !updatedRow) return;
        if (!state.dataByunique_key[unique_key]) {
          state.dataByunique_key[unique_key] = { rows: [] };
        }
        const groupData = state.dataByunique_key[unique_key];
        if (!Array.isArray(groupData.rows)) return;
        state.dataByunique_key[unique_key].rows = groupData.rows.map(row =>
          row.order_id == order_id ? { ...row, ...updatedRow } : row
        );
        state.updateRowLoading = false;
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.updateRowLoading = false;
        state.updateRowError = action.payload;
      })
      .addCase(updateSingleRow.fulfilled, (state, action) => {
        if (!action.payload) return;
        const { unique_key, order_id, updatedRow } = action.payload;
        const groupData = state.dataByunique_key[unique_key];
        if (!groupData || !Array.isArray(groupData.rows)) return;
        const existingRow = groupData.rows.find((row) => row.order_id == order_id);
        if (!existingRow) return;
        const updatedFullRow = { ...existingRow, ...updatedRow };
        groupData.rows = groupData.rows.map((row) =>
          row.order_id == order_id ? updatedFullRow : row
        );
      })
      .addCase(updateDynamicColumn.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDynamicColumn.fulfilled, (state, action) => {
        const { unique_key, order_id, column_name, new_value } = action.payload;
        state.loading = false;
        if (state.dataByunique_key[unique_key] && Array.isArray(state.dataByunique_key[unique_key].rows)) {
          state.dataByunique_key[unique_key].rows = state.dataByunique_key[unique_key].rows.map((row) =>
            row.order_id == order_id ? { ...row, [column_name]: new_value } : row
          );
        }
      })
      .addCase(updateDynamicColumn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error al actualizar la columna dinámica';
      })
      .addCase(fetchOrderStatuses.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchOrderStatuses.fulfilled, (state, action) => {
        state.orderStatuses = action.payload;
      })
      .addCase(fetchOrderStatuses.rejected, (state, action) => {
        state.orderStatusesError = action.payload;
      })
      .addCase(saveDashboardGroup.pending, (state) => {
        state.savingDashboardGroup = true;
        state.errorSavingDashboardGroup = null;
      })
      .addCase(saveDashboardGroup.fulfilled, (state) => {
        state.savingDashboardGroup = false;
      })
      .addCase(saveDashboardGroup.rejected, (state, action) => {
        state.savingDashboardGroup = false;
        state.errorSavingDashboardGroup = action.payload;
      })
      .addCase(fetchDashboardGroups.pending, (state) => {
        state.loadingDashboardGroups = true;
        state.errorDashboardGroups = null;
      })
      .addCase(fetchDashboardGroups.fulfilled, (state, action) => {
        state.loadingDashboardGroups = false;
        state.errorDashboardGroups = null;
        state.dashboardGroups = action.payload;
      })
      .addCase(fetchDashboardGroups.rejected, (state, action) => {
        state.loadingDashboardGroups = false;
        state.errorDashboardGroups = action.payload;
      });
  },
});

export const {
  setLoadingMode,
  setunique_key,
  setSelectedColumns,
  addSelectedColumn,
  removeSelectedColumn,
  toggleManualSelection,
  setData,
  setColumnSettings,
  setAllColumnSettings,
  setParameters,
  importarTablas,
  updateGroupColumns,
  setDashboardColumns,
  updateCellValue,
  updateColumnValue,
  updateRowData,
  clearDataForKey,
  updateSingleRowLocally,
  addRowToTable,
  setTotalRecords
} = columnsSlice.actions;

export {
  fetchDashboardGroups,
  createDashboardGroup,
  updateDashboardGroup,
  deleteDashboardGroup,
  fetchInitialData,
  fetchGroupData,
  fetchUnifiedGroupData,
  fetchAllColumns,
  fetchSavedTables,
  fetchColumnAliases,
  fetchColumnConfigurations,
  saveColumnAlias,
  saveColumnSettings,
  saveDashboardGroup,
  saveColumns,
  deleteSavedTable,
  updateOrderStatus,
  fetchOrderStatuses,
  setDataForProcedure,
  updateDynamicColumn,
  updateSingleRow,
  updateSingleRowLocally,
};

export default columnsSlice.reducer;
